import React, { useState, useEffect } from 'react';
import { Wand2, Check, AlertTriangle, RefreshCw, Loader2 } from 'lucide-react';
import MicButton from './MicButton';

/**
 * The approval gates. A film is walked forward one stage at a time:
 *
 *   brief -> script -> shots -> footage -> assembled
 *
 * ⚠️ EVERY ARROW PAST THE SCRIPT COSTS MONEY, which is the whole reason these
 * are gates and not steps. Generation is billed per clip and per character, so
 * nothing advances without somebody looking at what came back and pressing a
 * button.
 *
 * ⚠️ THE SCRIPT IS EDITABLE AT THE GATE AND THE EDIT IS WHAT COUNTS. What a
 * person accepted becomes the script the shots are built from and the captions
 * are burned from - not what the model first wrote.
 *
 * ⚠️ CLAIMS ARE SHOWN BEFORE THE SCRIPT IS APPROVED, not after. A tourism film
 * that states something false is a liability, and the moment to catch it is
 * while it is still words.
 */

const STAGES = [
  { id: 'brief', label: 'Brief' },
  { id: 'script', label: 'Script' },
  { id: 'shots', label: 'Shots' },
  { id: 'footage', label: 'Footage' },
  { id: 'planned', label: 'Assembled' },
];

// The stage column carries working states too; map them onto the five a person
// cares about.
const STAGE_INDEX = {
  brief: 0, scripting: 1, script_ready: 1,
  shotting: 2, shots_ready: 2,
  footage: 3, generating: 3, generating_failed: 3, planning: 4, planned: 4,
  assembling: 4, assembled: 4, assembling_failed: 4,
};

export function StageBar({ stage }) {
  const at = STAGE_INDEX[stage] === undefined ? 0 : STAGE_INDEX[stage];
  const working = ['scripting', 'shotting', 'generating', 'planning', 'assembling'].includes(stage);
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 0, margin: '2px 0 14px' }}>
      {STAGES.map((s, i) => (
        <React.Fragment key={s.id}>
          {i ? (
            <div style={{ flex: '0 0 26px', height: 2, background: i <= at ? 'var(--orange)' : 'var(--gray-200)' }} />
          ) : null}
          <div style={{
            display: 'inline-flex', alignItems: 'center', gap: 5,
            padding: '4px 11px', borderRadius: 13, fontSize: 11, fontWeight: 700,
            background: i < at ? '#e8f5ec' : i === at ? '#fff4e5' : 'var(--gray-100)',
            color: i < at ? '#1a7f37' : i === at ? '#b25e09' : 'var(--gray-500)',
            border: i === at ? '1px solid #f0c48a' : '1px solid transparent',
          }}>
            {i < at ? <Check size={11} />
              : (i === at && working) ? <Loader2 size={11} className="spin" /> : null}
            {s.label}
          </div>
        </React.Fragment>
      ))}
    </div>
  );
}

/**
 * The script gate. Read it, fix it, approve it - or send it back with a note.
 */
export function ScriptGate({ draft, busy, onApprove, onRewrite }) {
  const [text, setText] = useState(draft.script || '');
  const [notes, setNotes] = useState('');
  useEffect(() => { setText(draft.script || ''); }, [draft.script]);

  const words = text.trim() ? text.trim().split(/\s+/).length : 0;
  // 115-125 words a minute is the read speed the narration was directed at.
  const seconds = Math.round((words / 120) * 60);
  const claims = draft.claims || [];
  const issues = draft.brand_issues || [];
  const low = claims.filter((c) => c.confidence !== 'high');

  return (
    <div className="card">
      <div className="card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <span>Read the script</span>
        <MicButton title="Dictate into the script"
          onTranscript={(t) => setText((cur) => (cur ? `${cur.trimEnd()} ${t}` : t))} />
      </div>
      <div className="card-body">
        <textarea className="form-textarea" rows={9} value={text} onChange={(e) => setText(e.target.value)}
          style={{ fontSize: 14, lineHeight: 1.6 }} />
        <div style={{ fontSize: 11, color: 'var(--gray-500)', margin: '6px 0 14px' }}>
          {words} words, about {seconds} seconds read aloud. Edit freely - what you approve is what gets
          spoken and what the captions say.
        </div>

        {issues.length ? (
          <div style={{ background: '#fdecec', borderRadius: 6, padding: '10px 12px', marginBottom: 12 }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: '#c0392b', marginBottom: 4 }}>
              Brand rules flagged {issues.length}
            </div>
            {issues.map((i, n) => (
              <div key={n} style={{ fontSize: 12, color: '#c0392b' }}>{i.id}: {i.excerpt || i.message}</div>
            ))}
          </div>
        ) : null}

        {claims.length ? (
          <div style={{ border: '1px solid var(--gray-200)', borderRadius: 6, padding: '10px 12px', marginBottom: 14 }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--navy)', marginBottom: 6 }}>
              Facts it stated {low.length ? `— ${low.length} it is unsure of` : ''}
            </div>
            {claims.map((c, n) => (
              <div key={n} style={{ display: 'flex', gap: 8, fontSize: 12, marginBottom: 4 }}>
                <span style={{
                  flex: '0 0 auto', fontWeight: 700, fontSize: 10, padding: '1px 6px', borderRadius: 8,
                  background: c.confidence === 'high' ? '#e8f5ec' : '#fff4e5',
                  color: c.confidence === 'high' ? '#1a7f37' : '#b25e09',
                }}>{c.confidence}</span>
                <span style={{ color: 'var(--gray-700)' }}>
                  {c.claim}
                  <span style={{ color: 'var(--gray-500)' }}> — {c.basis}</span>
                </span>
              </div>
            ))}
            <div style={{ fontSize: 11, color: 'var(--gray-500)', marginTop: 6 }}>
              Anything not marked high is worth checking before this is published.
            </div>
          </div>
        ) : null}

        {draft.notes ? (
          <div style={{ fontSize: 12, color: 'var(--gray-500)', marginBottom: 12, fontStyle: 'italic' }}>{draft.notes}</div>
        ) : null}

        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          <input className="form-input" style={{ flex: 1, minWidth: 200 }} value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Change something? Say what, then Rewrite." />
          <MicButton title="Dictate the change"
            onTranscript={(t) => setNotes((cur) => (cur ? `${cur.trimEnd()} ${t}` : t))} />
          <button className="btn btn-outline" disabled={busy} onClick={() => onRewrite(notes)}>
            <RefreshCw size={13} style={{ verticalAlign: -2, marginRight: 5 }} />Rewrite
          </button>
          <button className="btn btn-primary" disabled={busy || !text.trim()} onClick={() => onApprove(text)}>
            <Check size={13} style={{ verticalAlign: -2, marginRight: 5 }} />Approve, write the shots
          </button>
        </div>
      </div>
    </div>
  );
}

/**
 * The shot gate. This is the last stop before anything is generated, so the
 * prompts are shown in full and are copyable - both because they are about to
 * be spent on, and because pasting them into Flow by hand is a supported way to
 * work.
 */
export function ShotsGate({ draft, busy, onApprove, onRewrite }) {
  const shots = draft.shots || [];
  const coverage = draft.coverage || {};
  const warnings = draft.shot_warnings || [];
  const [copied, setCopied] = useState(null);

  const copy = (text, n) => {
    navigator.clipboard.writeText(text).then(() => {
      setCopied(n);
      setTimeout(() => setCopied(null), 1500);
    }).catch(() => {});
  };

  return (
    <div className="card">
      <div className="card-header">{shots.length} shots — check these before anything is generated</div>
      <div className="card-body">
        {coverage.complete === false ? (
          <div style={{ background: '#fdecec', color: '#c0392b', borderRadius: 6, padding: '9px 12px', marginBottom: 12, fontSize: 12 }}>
            These shots do not cover the whole script. Narration with no picture behind it would run over
            the shot before it. Missing: {(coverage.missingWords || []).join(', ')}
          </div>
        ) : null}
        {warnings.map((w, i) => (
          <div key={i} style={{ background: '#fff4e5', color: '#b25e09', borderRadius: 6, padding: '9px 12px', marginBottom: 8, fontSize: 12 }}>
            <AlertTriangle size={12} style={{ verticalAlign: -2, marginRight: 5 }} />{w}
          </div>
        ))}

        {shots.map((s) => (
          <div key={s.number} style={{ borderTop: '1px solid var(--gray-200)', padding: '12px 0' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 10 }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--navy)' }}>
                Shot {s.number} <span style={{ fontWeight: 400, color: 'var(--gray-500)' }}>— {s.purpose}</span>
              </div>
              <button className="btn btn-ghost" style={{ fontSize: 11, padding: '2px 8px' }}
                onClick={() => copy(s.prompt, s.number)}>
                {copied === s.number ? 'copied' : 'copy prompt'}
              </button>
            </div>
            <div style={{ fontSize: 12, fontStyle: 'italic', color: 'var(--gray-700)', margin: '4px 0 6px' }}>
              &ldquo;{s.script_text}&rdquo;
            </div>
            <div style={{ fontSize: 12, color: 'var(--gray-700)', background: 'var(--gray-100)', padding: '8px 10px', borderRadius: 5, lineHeight: 1.5 }}>
              {s.prompt}
            </div>
            {s.continuity ? (
              <div style={{ fontSize: 11, color: '#b25e09', marginTop: 5 }}>continuity: {s.continuity}</div>
            ) : null}
          </div>
        ))}

        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 14 }}>
          <button className="btn btn-outline" disabled={busy} onClick={() => onRewrite()}>
            <RefreshCw size={13} style={{ verticalAlign: -2, marginRight: 5 }} />Rewrite the shots
          </button>
          <button className="btn btn-primary" disabled={busy || !shots.length} onClick={onApprove}>
            <Check size={13} style={{ verticalAlign: -2, marginRight: 5 }} />Approve the shot list
          </button>
        </div>
      </div>
    </div>
  );
}

/**
 * Where footage comes from. Generation is not wired yet, so this says so
 * plainly and describes the path that does work rather than showing a button
 * that cannot do anything.
 */
export function FootageGate({ draft, busy, progress, takes = [], onGenerate, onOpenAssemble, children }) {
  const p = progress || {};
  const running = !!p.generating;
  const shots = draft.shots || [];
  const generateCount = shots.filter((s) => s.source !== 'library').length;
  const libraryCount = shots.length - generateCount;
  return (
    <div className="card">
      <div className="card-header">Footage</div>
      <div className="card-body">
        <p style={{ fontSize: 13, lineHeight: 1.6, margin: '0 0 12px', color: 'var(--gray-700)' }}>
          The shot list is approved. {shots.length} shot{shots.length === 1 ? '' : 's'} needed.
          {takes.length ? ` ${takes.filter((t) => t.ok).length} generated so far.` : ''}
        </p>
        <div style={{ background: 'var(--gray-100)', borderRadius: 6, padding: '10px 12px', fontSize: 12, color: 'var(--gray-700)', marginBottom: 14 }}>
          {generateCount} shot{generateCount === 1 ? '' : 's'} to generate at 8 seconds each.
          <strong> About ${(generateCount * 0.8).toFixed(2)}.</strong>
          {libraryCount ? ` ${libraryCount} shot${libraryCount === 1 ? ' comes' : 's come'} from the photo library instead - we do not generate the product.` : ''}
        </div>
        {running || p.done ? (
          <div style={{ border: '1px solid var(--gray-200)', borderRadius: 6, padding: '12px 14px', marginBottom: 14 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
              {running ? <Loader2 size={14} className="spin" color="var(--navy)" /> : <Check size={14} color="#1a7f37" />}
              <strong style={{ fontSize: 13, color: 'var(--navy)' }}>
                {running ? `Generating shot ${Math.min(p.done + 1, p.total)} of ${p.total}` : `${p.done} of ${p.total} shots generated`}
              </strong>
              <span style={{ fontSize: 12, color: 'var(--gray-500)' }}>${(p.spent || 0).toFixed(2)} so far</span>
            </div>
            <div style={{ height: 6, background: 'var(--gray-200)', borderRadius: 3, overflow: 'hidden' }}>
              <div style={{ height: '100%', width: `${p.total ? (p.done / p.total) * 100 : 0}%`, background: 'var(--orange)', transition: 'width .4s' }} />
            </div>
            {running ? (
              <div style={{ fontSize: 11, color: 'var(--gray-500)', marginTop: 7 }}>
                About a minute a shot. Leave the page if you like, it keeps going. Pressing the button
                again does nothing while a run is in flight.
              </div>
            ) : null}
            {p.failed ? (
              <div style={{ fontSize: 12, color: '#b25e09', marginTop: 7 }}>
                {p.failed} shot{p.failed === 1 ? '' : 's'} failed. Generating again retries only those.
              </div>
            ) : null}
          </div>
        ) : null}

        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 14 }}>
          <button className="btn btn-primary" disabled={busy || running || !generateCount} onClick={() => onGenerate('veo')}>
            <Wand2 size={13} style={{ verticalAlign: -2, marginRight: 5 }} />
            {running ? 'Generating…' : busy ? 'Working…' : p.done ? 'Generate the rest' : 'Generate the footage'}
          </button>
          <button className="btn btn-outline" disabled={busy || running || !generateCount} onClick={() => onGenerate('sora')}>
            Use Sora instead
          </button>
        </div>
        <div style={{ fontSize: 11, color: 'var(--gray-500)', marginBottom: 14 }}>
          Veo Fast and Sora 2 cost the same. Veo is about twice as quick and has better light; Sora has
          been better on period costume. If a shot comes back wrong, regenerate that one on the other.
        </div>
        {children}

        <details style={{ marginBottom: 14 }}>
          <summary style={{ fontSize: 12, color: 'var(--gray-500)', cursor: 'pointer' }}>Or do it by hand in Google Flow</summary>
          <ol style={{ fontSize: 13, lineHeight: 1.8, color: 'var(--gray-700)', paddingLeft: 20, margin: '8px 0 0' }}>
            <li>Copy each prompt above into Flow and download the take you like.</li>
            <li>Upload them under Marketing &gt; Media Intake, under this film&rsquo;s brand.</li>
            <li>Come back and choose them, in shot order.</li>
          </ol>
        </details>
        <button className="btn btn-ghost" style={{ fontSize: 12 }} onClick={onOpenAssemble}>
          Or attach clips from the library by hand
        </button>
      </div>
    </div>
  );
}

export default { StageBar, ScriptGate, ShotsGate, FootageGate };
